// Popup.jsx (최종 통합본 - 칸 크기 고정 + 1~2장 중앙정렬 최적화)
import React, { useEffect, useState, useRef, useMemo } from 'react';
import './Popup.css';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import axios from 'axios';

const ArrowIcon = ({ direction = 'left' }) => {
  const isLeft = direction === 'left';
  return (
    <svg className="popup-arrow-ic" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      {isLeft ? (
        <path
          d="M14.5 5.5L8 12l6.5 6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M9.5 5.5L16 12l-6.5 6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
};

const Popup = () => {
  const [popups, setPopups] = useState([]);
  const [showPopup, setShowPopup] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const roots = 'https://ciuc.or.kr:8443/';

  const sliderRef = useRef(null);

  useEffect(() => {
    fetchPopups();
  }, []);

  useEffect(() => {
    const hiddenUntil = localStorage.getItem('popupHiddenUntil');
    if (hiddenUntil) {
      const today = new Date().toISOString().slice(0, 10);
      if (hiddenUntil === today) setShowPopup(false);
    }
  }, []);

  const fetchPopups = async () => {
    try {
      const response = await axios.get('https://ciuc.or.kr:8443/api/get/popup_print');
      setPopups(response.data.data || []);
    } catch (error) {
      console.error('Error fetching popups:', error);
      setPopups([]);
    }
  };

  const handleClosePopup = () => setShowPopup(false);

  const handleHideForToday = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem('popupHiddenUntil', today);
    setShowPopup(false);
  };

  const handleTogglePlay = () => {
    if (!sliderRef.current) return;
    if (isPlaying) sliderRef.current.slickPause();
    else sliderRef.current.slickPlay();
    setIsPlaying((prev) => !prev);
  };

  const handlePrev = () => sliderRef.current?.slickPrev();
  const handleNext = () => sliderRef.current?.slickNext();

  const numPopups = popups.length;

  /**
   * ✅ 핵심 정책
   * - "칸 크기"는 고정: PC=3, Tablet=2, Mobile=1
   * - 다만 등록된 이미지 수가 적으면(1~2) infinite/autoplay 끄고,
   *   CSS로 slick-track을 가운데 정렬해서 '빈칸'이 양옆으로 분배되게만 한다.
   */
  const settings = useMemo(() => {
    return {
      dots: numPopups > 1,
      arrows: false,

      // ✅ 칸 고정 (PC)
      slidesToShow: 3,
      slidesToScroll: 1,

      // ✅ 복제/양쪽 이상 출력 방지: 슬라이드가 3장 이하이면 무한루프/자동재생 OFF
      infinite: numPopups > 3,
      autoplay: numPopups > 3,

      speed: 600,
      autoplaySpeed: 3000,
      cssEase: 'ease-in-out',

      responsive: [
        {
          breakpoint: 1024,
          settings: {
            // ✅ 칸 고정 (Tablet)
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: numPopups > 2,
            autoplay: numPopups > 2,
            dots: numPopups > 1,
          },
        },
        {
          breakpoint: 768,
          settings: {
            // ✅ 칸 고정 (Mobile)
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: numPopups > 1,
            autoplay: numPopups > 1,
            dots: numPopups > 1,
          },
        },
      ],
    };
  }, [numPopups]);

  if (!showPopup || numPopups === 0) return null;

  return (
    <div className="popup-container">
      <div className="popup-content">
        {/* ✅ 2장 이상일 때만 화살표 노출 */}
        {numPopups > 1 && (
          <>
            <button type="button" className="popup-arrow popup-arrow-prev" onClick={handlePrev} aria-label="이전">
              <ArrowIcon direction="left" />
            </button>

            <button type="button" className="popup-arrow popup-arrow-next" onClick={handleNext} aria-label="다음">
              <ArrowIcon direction="right" />
            </button>
          </>
        )}

        {/* ✅ count-* 클래스는 CSS에서 "1~2장일 때 중앙정렬" 처리용 */}
        <div className={`popup-slider-wrap count-${Math.min(numPopups, 9)}`}>
          <Slider ref={sliderRef} {...settings}>
            {popups.map((popup, index) => {
              const img = (
                <div className="popup-frame" style={{ '--bg': `url(${roots}${popup.file_url})` }}>
                  <img src={`${roots}${popup.file_url}`} alt={popup.title || 'popup'} loading="lazy" />
                </div>
              );

              return (
                <div key={popup?.idx ?? index} className="popup">
                  {popup.url ? (
                    <a href={popup.url} target="_blank" rel="noopener noreferrer">
                      {img}
                    </a>
                  ) : (
                    img
                  )}
                </div>
              );
            })}
          </Slider>
        </div>

        <div className="popup-buttons">
          {/* ✅ PC 기준 3장 초과일 때만 자동재생 버튼 */}
          {numPopups > 3 && (
            <button className="btn-outline" onClick={handleTogglePlay}>
              {isPlaying ? '일시정지' : '자동재생'}
            </button>
          )}
          <button className="btn-soft" onClick={handleHideForToday}>
            오늘 하루 보지 않기
          </button>
          <button className="btn-primary" onClick={handleClosePopup}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default Popup;
